import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { easeOut, panel } from "../motion";
import { IconChevron } from "./icons";
import { useMenuPosition } from "../hooks/useMenuPosition";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  id?: string;
  /** Mirrors the native aria-label attribute. */
  ariaLabel?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Text shown when no (enabled) option is selected. */
  placeholder?: string;
  /** Allow typing a custom value not in `options` (combobox behaviour): the
   *  menu gains a search box and a "Create \"…\"" entry for unmatched text. */
  creatable?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * Themed replacement for the native <select>. Reuses the existing
 * .dropdown / .dropdown-menu / .dropdown-item visual language so it stays
 * consistent with the rest of the app.
 *
 * Keyboard: Enter/Space/ArrowDown opens, Up/Down moves, Enter chooses,
 * Escape closes — the same model as a native control.
 */
export function Select({ id, ariaLabel, value, onChange, options, placeholder, creatable = false, className, style }: SelectProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [draft, setDraft] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const openAtRef = useRef(0);
  const fallbackId = useId();

  const selected = options.find((o) => o.value === value && !o.disabled);
  const display = selected ? selected.label : creatable && value ? value : (placeholder ?? "Select…");
  // Creatable filtering: live substring match while the menu is open; unmatched
  // text becomes a "Create \"…\"" entry so any custom label can be added.
  const query = draft.trim().toLowerCase();
  const filtered = creatable && open
    ? options.filter((o) => !o.disabled && (o.label.toLowerCase().includes(query) || o.value.toLowerCase().includes(query)))
    : options;
  const canCreate = creatable && open && query !== "" && filtered.length === 0;

  // Fixed viewport-clamped placement (escapes scroll-container clipping) plus
  // re-clamp on resize and on menu content changes (creatable search).
  // The inline min-width: 0 pins the trigger-derived width — once fixed,
  // .select-menu's min-width (max(100%, 160px)) would resolve against the
  // viewport instead.
  const menuPos = useMenuPosition(open, wrapRef, menuRef, filtered.length + (canCreate ? 1 : 0));

  // Stamp when the menu opens so the scroll-to-close handler can tell the
  // browser's immediate focus-scroll (short screens) from a real dismissal.
  useEffect(() => {
    if (open) openAtRef.current = performance.now();
  }, [open]);

  // A real container scroll would leave the fixed menu stranded over the wrong
  // spot, so dismiss it. Ignore the menu's own inner list scroll (long option
  // lists) and the layout-viewport scroll Android fires when the soft keyboard
  // opens (that one leaves the fixed anchor exactly where it is). Also ignore
  // scrolls in the opening instant: on short screens the browser focus-scrolls
  // a tapped field into view right after the menu opens, which is not the user
  // dismissing it.
  useEffect(() => {
    if (!open) return;
    function onScroll(e: Event) {
      const t = e.target as Node | null;
      if (t && menuRef.current && menuRef.current.contains(t)) return;
      if (t === document || (document.scrollingElement && t === document.scrollingElement)) return;
      if (performance.now() - openAtRef.current < 250) return;
      setOpen(false);
    }
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Focus the search box when a creatable menu opens so typing works immediately —
  // but only when a hardware keyboard is plausible. Auto-focusing on touch /
  // coarse-pointer devices pops the soft keyboard over the menu unprompted
  // (and, inside popovers, fires the viewport resize/scroll that can dismiss
  // them) — the user can tap the box to type a custom value instead.
  useEffect(() => {
    if (open && creatable && window.matchMedia?.("(pointer: coarse)").matches === false) {
      inputRef.current?.focus();
    }
  }, [open, creatable]);

  // Reset highlight to the current selection each time the menu opens.
  useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => o.value === value && !o.disabled);
    setActive(idx >= 0 ? idx : options.findIndex((o) => !o.disabled));
  }, [open]); // options/value are stable per render usage

  // Keep the highlighted option in view when the menu is open (long lists
  // scroll). Scroll only the menu's own list — scrollIntoView would also
  // scroll ancestor containers, which on a short viewport both moves the page
  // under the fixed menu and trips the scroll-to-close handler.
  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    const el = activeRef.current;
    if (!menu || !el) return;
    const mr = menu.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    if (er.top < mr.top) menu.scrollTop -= mr.top - er.top;
    else if (er.bottom > mr.bottom) menu.scrollTop += er.bottom - mr.bottom;
  }, [open, active]);

  function enabledIndices() {
    return options.map((o, i) => i).filter((i) => !options[i]!.disabled);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const first = enabledIndices()[0];
        setOpen(true);
        setActive(first ?? -1);
      }
      return;
    }
    const enabled = enabledIndices();
    const cur = enabled.indexOf(active);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(enabled[(cur + 1) % enabled.length] ?? -1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(enabled[(cur - 1 + enabled.length) % enabled.length] ?? -1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = options[active];
      if (o && !o.disabled) {
        onChange(o.value);
        setOpen(false);
      }
    }
  }

  function choose(o: SelectOption) {
    if (o.disabled) return;
    onChange(o.value);
    setOpen(false);
    setDraft("");
  }

  function chooseCustom(value: string) {
    onChange(value);
    setOpen(false);
    setDraft("");
  }

  // Keyboard navigation inside the search box: Up/Down move the highlight,
  // Enter picks the highlighted option or creates the typed custom value.
  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const items = filtered.length + (canCreate ? 1 : 0);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (items === 0 ? -1 : Math.min(a + 1, items - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (canCreate && (active === filtered.length || filtered.length === 0)) chooseCustom(draft.trim());
      else if (active >= 0 && active < filtered.length) choose(filtered[active]!);
    } else if (e.key === "Escape") {
      setOpen(false);
      setDraft("");
    }
  }

  return (
    <div className={`dropdown select-wrap ${className ?? ""}`} ref={wrapRef} style={style}>
      <button
        type="button"
        id={id ?? fallbackId}
        className="select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => { if (!open) setDraft(""); setOpen((o) => !o); }}
        onKeyDown={onKeyDown}
      >
        <span className={selected ? "" : "muted"}>{display}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2, ease: easeOut }}
          style={{ display: "inline-flex", marginLeft: "auto" }}
        >
          <IconChevron />
        </motion.span>
      </button>
      <AnimatePresence>
        {open ? (
          <motion.div
            ref={menuRef}
            className="dropdown-menu select-menu"
            role="listbox"
            style={menuPos ? { position: "fixed", left: menuPos.left, top: menuPos.top, width: menuPos.width, minWidth: 0 } : undefined}
            variants={panel}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            {creatable ? (
              <div className="select-search-wrap">
                <input
                  ref={inputRef}
                  className="select-search"
                  aria-label="Type to filter or add"
                  placeholder="Type to filter or add…"
                  value={draft}
                  onChange={(e) => { setDraft(e.target.value); setActive(0); }}
                  onKeyDown={onSearchKeyDown}
                />
              </div>
            ) : null}
            {filtered.map((o, i) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                disabled={o.disabled}
                ref={i === active ? activeRef : null}
                className={`dropdown-item ${o.value === value ? "selected" : ""} ${i === active ? "active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(o)}
              >
                {o.label}
              </button>
            ))}
            {canCreate ? (
              <button
                type="button"
                role="option"
                aria-selected={false}
                ref={filtered.length === active ? activeRef : null}
                className={`dropdown-item create-option ${filtered.length === active ? "active" : ""}`}
                onMouseEnter={() => setActive(filtered.length)}
                onClick={() => chooseCustom(draft.trim())}
              >
                Create “{draft.trim()}”
              </button>
            ) : null}
            {open && filtered.length === 0 && !canCreate ? <div className="dropdown-empty">No options</div> : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
